import moment from 'moment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { generateHeader } from 'lib/pdfs/Common';
import { formatNumber, formatMoney } from 'accounting-js';

// Get Current Date
const today = new Date();
const currentYear = today.getFullYear();
const currentMonth = today.getMonth() + 1;

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const getDepreciation = (type, record) => {
  switch (type) {
    case 'fiscal':
      return record._fiscalDepreciationResult || {};
    case 'financial':
      return record._financialDepreciationResult || {};
    default:
      return true;
  }
};

export const generateDepreciationPDF = async (subcompany, endDate, type, records) => {
  const header = await generateHeader(`${subcompany.name} \nReporte de depreciación`);

  const rows = records.map((r) => {
    const depreciation = getDepreciation(type, r);

    // Subtract this.props.asset.startDate - today
    const diff = moment(today).diff(moment(r.startDate), 'months');

    return [
      { text: r.number, style: 'tableRow' },
      { text: r.name, style: 'tableRow' },
      { text: r.brand || '-', style: 'tableRow' },
      { text: r.model || '-', style: 'tableRow' },
      { text: r.serie || '-', style: 'tableRow' },
      {
        text: r._costCenter ? `${r._costCenter.key} - ${r._costCenter.name}` : '-',
        style: 'tableRow',
      },
      {
        text: r._account ? `${r._account.key}` : '-',
        style: 'tableRow',
      },
      {
        text: r._account ? `${r._account.name}` : '-',
        style: 'tableRow',
      },
      {
        text: r.buyDate ? moment(r.buyDate).format('DD/MM/YYYY') : '-',
        style: 'tableRow',
      },
      {
        text: r.startDate ? moment(r.startDate).format('DD/MM/YYYY') : '-',
        style: 'tableRow',
      },
      { text: depreciation.name || '-', style: 'tableRow' },
      { text: formatMoney(r.price || 0), style: 'tableRow' },
      { text: formatMoney(r.dollarAssetPrice || 0), style: 'tableRow' },
      {
        text: formatNumber(depreciation.totalLifeYears || 0, { precision: 4 }) || '-',
        style: 'tableRow',
      },
      {
        text: formatNumber(
          depreciation.factors && depreciation.factors.acumulado < depreciation.totalLifeMonths
            ? depreciation.factors.countMonthDep
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      {
        text: formatNumber(
          depreciation.factors
            ? formatNumber(
                depreciation.factors.acumulado < depreciation.totalLifeMonths
                  ? depreciation.factors.acumulado - depreciation.factors.countMonthDep
                  : depreciation.factors.acumulado > depreciation.totalLifeMonths
                  ? depreciation.factors.acumulado - 1
                  : depreciation.factors.acumulado
              )
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      // T. months dep. in exercises at the current
      {
        text: formatNumber(
          depreciation.factors
            ? formatNumber(
                depreciation.factors.acumulado < depreciation.totalLifeMonths
                  ? depreciation.factors.acumulado
                  : diff - 1 > depreciation.factors.acumulado
                  ? 0
                  : 0
              )
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      {
        text: formatNumber(depreciation.totalLifeMonths || 0, { precision: 0 }),
        style: 'tableRow',
      },
      {
        text: formatNumber(depreciation.lifeYears || 0, { precision: 4 }),
        style: 'tableRow',
      },
      {
        text: formatMoney(depreciation.originalAmount || 0),
        style: 'tableRow',
      },
      {
        text: formatMoney(
          depreciation.factors && depreciation.factors.acumulado < depreciation.totalLifeMonths
            ? depreciation.lastPeriodDepreciation
            : depreciation.lastPeriodDepreciation + depreciation.currentNormalDepreciation || 0
        ),
        style: 'tableRow',
      },
      {
        text: formatMoney(
          depreciation.factors
            ? depreciation.factors.acumulado < depreciation.totalLifeMonths
              ? depreciation.currentNormalDepreciation
              : diff - 1 > depreciation.factors.acumulado
              ? 0
              : depreciation.currentNormalDepreciation
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      {
        text: formatMoney(
          depreciation.factors
            ? depreciation.factors.acumulado < depreciation.totalLifeMonths
              ? depreciation.depMonthly
              : diff - 1 > depreciation.factors.acumulado
              ? 0
              : depreciation.depMonthly
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      {
        text: formatMoney(
          depreciation.factors
            ? depreciation.factors.acumulado < depreciation.totalLifeMonths
              ? depreciation.depMonthlyUsd
              : diff - 1 > depreciation.factors.acumulado
              ? 0
              : depreciation.depMonthlyUsd
            : 0,
          {
            precision: 0,
          }
        ),
        style: 'tableRow',
      },
      {
        text: formatNumber((depreciation.factors && depreciation.factors.inpcBegin) || 0, {
          precision: 4,
        }),
        style: 'tableRow',
      },
      {
        text: formatNumber((depreciation.factors && depreciation.factors.inpcEnd) || 0, {
          precision: 4,
        }),
        style: 'tableRow',
      },
      {
        text: formatNumber((depreciation.factors && depreciation.factors.factor) || 0, {
          precision: 4,
        }),
        style: 'tableRow',
      },
      {
        text: formatMoney(depreciation.currentDepreciation || 0),
        style: 'tableRow',
      },
      {
        text: formatMoney(depreciation.totalDepreciation || 0),
        style: 'tableRow',
      },
      { text: formatMoney(depreciation.depreciable || 0), style: 'tableRow' },
    ];
  });

  const docDefinition = {
    pageOrientation: 'landscape',
    pageSize: 'TABLOID',
    content: [
      header,
      {
        margin: [0, 10, 0, 0],
        columnGap: 20,
        columns: [
          {
            width: '*',
            text: `Fecha de valores: ${moment(endDate).format('DD/MM/YYYY')}`,
            bold: true,
            style: 'textStyle',
          },
          {
            width: '100',
            text: `Fecha: ${moment().format('DD/MM/YYYY')}`,
            style: 'textStyle',
          },
        ],
      },
      {
        margin: [0, 20, 0, 0],
        table: {
          headerRows: 1,
          widths: [
            '4%',
            '4%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
            '4%',
            '4%',
            '4%',
            '3%',
            '4%',
            '4%',
            '4%',
            '4%',
            '4%',
            '3%',
            '3%',
            '3%',
            '3%',
            '3%',
          ],
          body: [
            [
              { text: 'Número', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Nombre', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Marca', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Modelo', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Serie', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'Centro de costos',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Cuenta(Clave)',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Cuenta(Nombre)',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Fecha de compra',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Fecha de aplicación',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Depreciación aplicada',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              { text: 'Precio', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Precio USD', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Vida útil', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Meses dep. periodo actual', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'Acumulado de meses dep. en ejer ant',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Acumulado de meses dep. en ejer ant. al act',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              { text: 'Total de meses a depreciarse', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'Vida remanente',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              { text: 'MOI', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'Dep. periodo anterior',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Dep. periodo actual',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              { text: 'Depreciación mensual', style: 'tableRow', bold: true, fontSize: 7 },
              { text: 'Depreciación mensual USD', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'INPC compra',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'INPC utilización',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              { text: 'Factor', style: 'tableRow', bold: true, fontSize: 7 },
              {
                text: 'Dep. periodo actual recalculada',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Dep. total',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
              {
                text: 'Valor reposición',
                style: 'tableRow',
                bold: true,
                fontSize: 7,
              },
            ],
            ...rows,
          ],
        },
        layout: {
          hLineWidth: function (i, node) {
            return i === 0 || i === 1 ? 2 : 1;
          },
          vLineWidth: function (i, node) {
            return 0;
          },
          hLineColor: function (i, node) {
            return '#e9ecef';
          },
        },
      },
    ],
    styles: {
      textStyle: {
        color: '#45535b',
        fontSize: 11,
        margin: [0, 20, 0, 0],
      },

      tableRow: {
        color: '#45535b',
        fontSize: 6,
        margin: [10, 5, 0, 5],
      },
      tableRowOdd: {
        color: '#45535b',
        fontSize: 6,
        fillColor: '#f4f4f4',
        margin: [10, 5, 0, 5],
      },
    },
  };

  pdfMake.createPdf(docDefinition).open();
};



// WEBPACK FOOTER //
// src/lib/pdfs/DepreciationPDF.js